/**
 * SIDE PANEL: FORM DETAIL
 * Panel latéral pour consultation/édition d'un formulaire
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Copy, Plus } from 'lucide-react';
import {
  Form,
  FormInput,
  FormSelect,
  FormSelectWithCreate,
  toast,
  Button,
  Label,
  Input,
  Toggle,
} from '../ui';
import { SidePanel, SidePanelFooter } from '../ui/side-panel';
import { FormFieldCard } from '../forms/FormFieldCard';
import { AddFieldModal } from '../modals/AddFieldModal';
import { CreateOriginModal } from '../modals/CreateOriginModal';
import type {
  Form as FormType,
  FormField,
  CreateFormFieldDto,
  FormFieldWithValues,
} from '../../types/form';
import { updateFormSchema, type UpdateFormInput } from '../../schemas/form';
import { getPublicFormUrl, getFormIframeCode } from '../../types/form';
import { useFormFields } from '../../hooks/useFormFields';
import { useProductsSelect } from '../../hooks/useProductsSelect';
import { useOrigins } from '../../hooks/useOrigins';
import { usePermissions } from '../../hooks/usePermissions';

interface FormDetailPanelProps {
  isOpen: boolean;
  onClose: () => void;
  form: FormType | null;
  onUpdate: (id: string, data: UpdateFormInput) => Promise<void>;
  onDelete?: (id: string) => Promise<void>;
}

export function FormDetailPanel({
  isOpen,
  onClose,
  form,
  onUpdate,
  onDelete,
}: FormDetailPanelProps) {
  const [isAddFieldModalOpen, setIsAddFieldModalOpen] = useState(false);
  const [isCreateOriginModalOpen, setIsCreateOriginModalOpen] = useState(false);
  const [localFields, setLocalFields] = useState<FormFieldWithValues[]>([]);
  const [hasFieldsChanged, setHasFieldsChanged] = useState(false);

  const { products } = useProductsSelect();
  const { origins, createOrigin } = useOrigins();
  const { canDeleteEntity, canUpdateEntity } = usePermissions();

  // Mode lecture seule si pas de permission d'édition
  const isReadOnly = !canUpdateEntity('form');
  const {
    fields,
    loading: fieldsLoading,
    createField,
    reorderFields,
    deleteField,
  } = useFormFields({
    formId: form?.id,
    autoLoad: isOpen && !!form,
  });

  const formHook = useForm<UpdateFormInput>({
    resolver: zodResolver(updateFormSchema),
  });

  // Configuration des sensors pour le drag & drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Synchroniser les champs locaux avec les champs chargés
  useEffect(() => {
    setLocalFields(fields);
    setHasFieldsChanged(false);
  }, [fields]);

  // Réinitialiser le formulaire quand le form change
  useEffect(() => {
    if (form) {
      formHook.reset({
        name: form.name,
        product_id: form.product_id || '',
        origin_id: form.origin_id || '',
        is_active: form.is_active,
        form_type: form.form_type || 'simple',
        generates_bpi: form.generates_bpi ?? false,
        generates_bed: form.generates_bed ?? false,
      });
    }
  }, [form, formHook]);

  if (!form) return null;

  const publicUrl = getPublicFormUrl(form.slug);
  const iframeCode = getFormIframeCode(form.slug);

  const productOptions = products.map((p) => ({
    value: p.id,
    label: p.name,
  }));

  const originOptions = origins.map((origin) => ({
    value: origin.id,
    label: origin.label,
  }));

  const handleFormSubmit = async (data: UpdateFormInput) => {
    try {
      // 1. Mettre à jour les infos du formulaire
      await onUpdate(form.id, data);

      // 2. Si l'ordre des champs a changé, sauvegarder
      if (hasFieldsChanged) {
        const success = await reorderFields(localFields);
        if (!success) {
          toast.error('Erreur lors de la réorganisation des champs');
          return;
        }
      }

      toast.success('Formulaire mis à jour');
      setHasFieldsChanged(false);
      // Ne pas fermer le panel pour permettre à l'utilisateur de continuer à modifier
    } catch (error) {
      console.error('Erreur lors de la mise à jour du formulaire :', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleClose = () => {
    // Réinitialiser les champs locaux si des changements non sauvegardés
    if (hasFieldsChanged) {
      setLocalFields(fields);
      setHasFieldsChanged(false);
    }
    formHook.reset();
    onClose();
  };

  const handleDelete = async () => {
    if (onDelete) {
      await onDelete(form.id);
    }
  };

  async function handleCopy(text: string, label: string) {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copié dans le presse-papiers`);
    } catch {
      toast.error('Erreur lors de la copie');
    }
  }

  // Gestion du drag & drop (local seulement, sauvegarde au submit)
  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = localFields.findIndex((f) => f.id === active.id);
    const newIndex = localFields.findIndex((f) => f.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(localFields, oldIndex, newIndex);
    setLocalFields(reordered);
    setHasFieldsChanged(true);
  }

  // Ajout d'un nouveau champ
  async function handleAddField(
    fieldData: Omit<CreateFormFieldDto, 'form_id' | 'order_index'>,
    orderIndex?: number
  ) {
    if (!form) return;

    const newField: CreateFormFieldDto = {
      ...fieldData,
      form_id: form.id,
      order_index:
        orderIndex !== undefined
          ? orderIndex
          : fields.length > 0
            ? Math.max(...fields.map((f) => f.order_index)) + 1
            : 0,
    };

    const success = await createField(newField);
    if (!success) {
      throw new Error("Erreur lors de l'ajout du champ");
    }
    // Le toast est géré par le composant appelant (AddFieldModal)
  }

  // Suppression d'un champ
  async function handleDeleteField(field: FormField) {
    const success = await deleteField(field.id);
    if (success) {
      toast.success('Champ supprimé');
    } else {
      toast.error('Erreur lors de la suppression');
    }
  }

  return (
    <SidePanel
      isOpen={isOpen}
      onClose={handleClose}
      title={form.name}
      onDelete={canDeleteEntity('form') && onDelete ? handleDelete : undefined}
      footer={
        !isReadOnly ? (
          <SidePanelFooter
            onCancel={handleClose}
            submitLabel="Enregistrer"
            submitDisabled={hasFieldsChanged || formHook.formState.isDirty ? false : true}
            isSubmitting={formHook.formState.isSubmitting}
            formId="edit-form-form"
          />
        ) : undefined
      }
    >
      <Form {...formHook}>
        <form onSubmit={formHook.handleSubmit(handleFormSubmit)} id="edit-form-form">
          <div className="space-y-6">
            {/* Section Informations générales */}
            <div>
              <h3 className="text-sm font-semibold text-zinc-900 mb-4">Informations générales</h3>

              <div className="space-y-4">
                <FormInput
                  control={formHook.control}
                  name="name"
                  label="Nom du formulaire"
                  required
                  disabled={isReadOnly}
                />

                <FormSelect
                  control={formHook.control}
                  name="product_id"
                  label="Produit associé"
                  placeholder="Aucun produit"
                  options={productOptions}
                  disabled={isReadOnly}
                />

                <FormSelectWithCreate
                  control={formHook.control}
                  name="origin_id"
                  label="Origine par défaut"
                  placeholder="Aucune origine"
                  options={originOptions}
                  onCreateClick={isReadOnly ? undefined : () => setIsCreateOriginModalOpen(true)}
                  createButtonLabel="Nouvelle"
                  disabled={isReadOnly}
                />

                {/* Toggle pour activer/désactiver le formulaire */}
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Statut</Label>
                    <p className="text-sm text-zinc-500">
                      {formHook.watch('is_active')
                        ? 'Le formulaire est actif'
                        : 'Le formulaire est inactif'}
                    </p>
                  </div>
                  <Toggle
                    checked={formHook.watch('is_active') ?? false}
                    onChange={(checked) =>
                      formHook.setValue('is_active', checked, { shouldDirty: true })
                    }
                    disabled={isReadOnly}
                  />
                </div>

                {/* Toggle pour le mode duo (pilote + copilote) */}
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Formulaire duo</Label>
                    <p className="text-sm text-zinc-500">
                      {formHook.watch('form_type') === 'duo'
                        ? 'Crée 2 prospects (pilote + copilote)'
                        : 'Crée 1 prospect par soumission'}
                    </p>
                  </div>
                  <Toggle
                    checked={formHook.watch('form_type') === 'duo'}
                    onChange={(checked) =>
                      formHook.setValue('form_type', checked ? 'duo' : 'simple', {
                        shouldDirty: true,
                      })
                    }
                    disabled={isReadOnly}
                  />
                </div>

                {/* Toggle pour la génération de BPI */}
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Générer BPI</Label>
                    <p className="text-sm text-zinc-500">
                      Génère un bulletin de pré-inscription à l'envoi
                    </p>
                  </div>
                  <Toggle
                    checked={formHook.watch('generates_bpi') ?? false}
                    onChange={(checked) => {
                      formHook.setValue('generates_bpi', checked, {
                        shouldDirty: true,
                      });
                      // Exclusion mutuelle : activer BPI (acompte) désactive BED (solde)
                      if (checked) {
                        formHook.setValue('generates_bed', false, {
                          shouldDirty: true,
                        });
                      }
                    }}
                    disabled={isReadOnly}
                  />
                </div>

                {/* Toggle pour la génération de BED (solde) — symétrique de BPI */}
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Générer BED</Label>
                    <p className="text-sm text-zinc-500">
                      Génère un bulletin d'engagement définitif (solde) à l'envoi
                    </p>
                  </div>
                  <Toggle
                    checked={formHook.watch('generates_bed') ?? false}
                    onChange={(checked) => {
                      formHook.setValue('generates_bed', checked, {
                        shouldDirty: true,
                      });
                      // Exclusion mutuelle : activer BED (solde) désactive BPI (acompte)
                      if (checked) {
                        formHook.setValue('generates_bpi', false, {
                          shouldDirty: true,
                        });
                      }
                    }}
                    disabled={isReadOnly}
                  />
                </div>
              </div>
            </div>

            {/* Section Partage */}
            <div>
              <h3 className="text-sm font-semibold text-zinc-900 mb-4">Partage</h3>

              <div className="space-y-4">
                {/* URL publique */}
                <div className="space-y-2">
                  <Label>URL publique</Label>
                  <div className="flex gap-2">
                    <Input value={publicUrl} readOnly className="flex-1 text-sm" />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleCopy(publicUrl, 'Lien')}
                      className="shrink-0"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* Code iframe */}
                <div className="space-y-2">
                  <Label>Code iframe</Label>
                  <div className="relative">
                    <textarea
                      value={iframeCode}
                      readOnly
                      className="w-full h-24 px-3 py-2 text-xs bg-zinc-50 border border-zinc-200 rounded-md resize-none font-mono"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleCopy(iframeCode, 'Code iframe')}
                      className="absolute top-2 right-2"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Section Champs du formulaire */}
            <div>
              <h3 className="text-sm font-semibold text-zinc-900 mb-4">Champs du formulaire</h3>

              {fieldsLoading ? (
                <div className="bg-zinc-50 border border-zinc-200 rounded-md p-4 text-sm text-zinc-500">
                  Chargement des champs...
                </div>
              ) : localFields.length === 0 ? (
                <div className="bg-zinc-50 border border-zinc-200 rounded-md p-4 text-sm text-zinc-500">
                  Aucun champ pour le moment. Cliquez sur "Ajouter un champ" pour commencer.
                </div>
              ) : (
                <>
                  {hasFieldsChanged && (
                    <div className="mb-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700">
                      💡 L'ordre des champs a été modifié. Cliquez sur "Enregistrer" pour
                      sauvegarder.
                    </div>
                  )}
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={localFields.map((f) => f.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="space-y-2">
                        {localFields.map((field) => (
                          <FormFieldCard
                            key={field.id}
                            field={field}
                            onDelete={isReadOnly ? undefined : handleDeleteField}
                            disabled={isReadOnly}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                </>
              )}

              {!isReadOnly && (
                <Button
                  type="button"
                  variant="outline"
                  className="w-full mt-3"
                  onClick={() => setIsAddFieldModalOpen(true)}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Ajouter un champ
                </Button>
              )}
            </div>
          </div>
        </form>
      </Form>

      {/* Modal d'ajout de champ */}
      <AddFieldModal
        isOpen={isAddFieldModalOpen}
        onClose={() => setIsAddFieldModalOpen(false)}
        onAdd={handleAddField}
        currentFieldsCount={
          fields.length > 0 ? Math.max(...fields.map((f) => f.order_index)) + 1 : 0
        }
        existingFields={fields}
      />

      {/* Modal de création d'origine */}
      <CreateOriginModal
        isOpen={isCreateOriginModalOpen}
        onClose={() => setIsCreateOriginModalOpen(false)}
        onSubmit={async (data) => {
          const newOrigin = await createOrigin(data);
          if (newOrigin) {
            formHook.setValue('origin_id', newOrigin.id, { shouldDirty: true });
          }
        }}
      />
    </SidePanel>
  );
}
